import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { usersApi, userOrganisationsApi } from '../services/userOrganisations';
import { logout } from './authSlice';

export const fetchUsers = createAsyncThunk(
    'userOrganisations/fetchList',
    async (_, { rejectWithValue }) => {
        try {
            return await usersApi.getList();
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchUsersByOrganisation = createAsyncThunk(
    'userOrganisations/fetchUsersByOrganisation',
    async (organisationId, { rejectWithValue, getState }) => {
        try {
            const response = await userOrganisationsApi.getListUsersByOrganisationId(organisationId);
            return { organisationId, data: response };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchUsersByOrganisations = createAsyncThunk(
    'userOrganisations/fetchUsersByOrganisations',
    async (organisationIds, { rejectWithValue }) => {
        try {
            const response = await userOrganisationsApi.getListUsersByOrganisationIds(organisationIds);
            return { organisationIds, data: response };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

const initialState = {
    list: [],
    organisationUsers: [],
    current: null,
    isLoading: false,
    error: null
};

const userOrganisationsSlice = createSlice({
    name: 'userOrganisations',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.current = null;
            state.error = null;
        },
        clearOrganisationUsers: (state) => {
            state.organisationUsers = [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchUsersByOrganisation.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUsersByOrganisation.fulfilled, (state, action) => {
                state.isLoading = false;
                const { organisationId, data } = action.payload;

                // API уже возвращает полные данные пользователей
                // Просто добавляем их в organisationUsers
                state.organisationUsers = data.map(user => ({
                    ...user,
                    // Оставляем оригинальное поле role и добавляем organisation_role для совместимости
                    organisation_role: user.role,
                    organisation_id: user.organisation_id || organisationId
                }));
            })
            .addCase(fetchUsersByOrganisation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Fetch users by multiple organisations
            .addCase(fetchUsersByOrganisations.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUsersByOrganisations.fulfilled, (state, action) => {
                state.isLoading = false;
                const { data } = action.payload;

                // API возвращает пользователей из всех выбранных организаций
                // Удаляем дубликаты по user_id
                const uniqueUsers = [];
                const seenUserIds = new Set();

                data.forEach(user => {
                    if (!seenUserIds.has(user.user_id)) {
                        seenUserIds.add(user.user_id);
                        uniqueUsers.push({
                            ...user,
                            // Оставляем оригинальное поле role и добавляем organisation_role для совместимости
                            organisation_role: user.role
                        });
                    }
                });

                state.organisationUsers = uniqueUsers;
            })
            .addCase(fetchUsersByOrganisations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.list = [];
                state.current = null;
                state.organisationUsers = [];
                state.loading = false;
                state.isLoading = false;
                state.error = null;
            })
    }
});

export const { clearCurrent, clearOrganisationUsers } = userOrganisationsSlice.actions;
export const userOrganisationsReducer = userOrganisationsSlice.reducer; 